import {
  PayPalCardFieldsProvider,
  PayPalNameField,
  PayPalNumberField,
  PayPalExpiryField,
  PayPalCVVField
} from '@paypal/react-paypal-js';
import { Col, Container, Row, TextInput } from '@paypalcorp/pp-react';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { CARD_FIELDS_SNIPPET_KEYS } from '../../../constants/sdk';
import SubmitPayment from '../SubmitPayment';
import IntegrationPageSectionContext from '../../IntegrationPageSection/StateManagement/IntegrationPageSectionContext';
import styles from '../../IntegrationPageSection/PreviewWrapper/Preview.module.less';

const CustomizedCardFieldsAndBillingAddress = (props) => {
  const { handleCreateOrder, handleOrderIntent } = props;
  const {
    cardFieldsConfigurator: { value: cardFieldsConfigurator },
    code: { status: { handleBillingAddress: billingAddressToggle } = {} } = {},
    OrderConfigurator: { threeDsSetting, amount: { value: orderAmount } = {}, sellerId } = {},
    SdkConfigurator: { sdkSettings }
  } = useContext(IntegrationPageSectionContext);
  const [isPaying, setIsPaying] = useState(false);
  const [cardFieldsLoading, setCardFieldsLoading] = useState(true);

  const [billingAddress, setBillingAddress] = useState({
    addressLine1: '',
    addressLine2: '',
    adminArea1: '',
    adminArea2: '',
    countryCode: '',
    postalCode: ''
  });

  useEffect(() => {
    setCardFieldsLoading(true);
    setTimeout(() => {
      setCardFieldsLoading(false);
    }, 1);
  }, [cardFieldsConfigurator, sdkSettings, threeDsSetting, orderAmount, sellerId]);

  useEffect(() => {
    setBillingAddress({
      addressLine1: '',
      addressLine2: '',
      adminArea1: '',
      adminArea2: '',
      countryCode: '',
      postalCode: ''
    });
  }, [billingAddressToggle]);

  const parentStyles = cardFieldsConfigurator[CARD_FIELDS_SNIPPET_KEYS.PARENT] || {};

  const getIndividualFieldStyles = (snippetKey) => {
    const individualStyles = cardFieldsConfigurator[snippetKey] || {};
    return {
      input: {
        ...(parentStyles.input || {}),
        ...(individualStyles.input || {})
      },
      '.invalid': {
        ...(parentStyles['.invalid'] || {}),
        ...(individualStyles['.invalid'] || {})
      }
    };
  };

  const handleBillingAddressChange = (field, value) => {
    setBillingAddress((prevAddress) => ({
      ...prevAddress,
      [field]: value
    }));
  };

  return (
    <>
      {!cardFieldsLoading && (
        <PayPalCardFieldsProvider
          createOrder={(params) =>
            handleCreateOrder({
              ...params,
              threeDsSetting
            })
          }
          onApprove={handleOrderIntent}
          style={parentStyles}
        >
          <Container form className={styles.card_fields_provider}>
            <Row>
              <Col form="full">
                <PayPalNameField style={getIndividualFieldStyles(CARD_FIELDS_SNIPPET_KEYS.NAME)} />
                <PayPalNumberField
                  style={getIndividualFieldStyles(CARD_FIELDS_SNIPPET_KEYS.NUMBER)}
                />
                <Row>
                  <Col>
                    <PayPalExpiryField
                      style={getIndividualFieldStyles(CARD_FIELDS_SNIPPET_KEYS.EXPIRY_DATE)}
                    />
                  </Col>
                  <Col>
                    <PayPalCVVField
                      style={getIndividualFieldStyles(CARD_FIELDS_SNIPPET_KEYS.CVV)}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
            {billingAddressToggle && (
              <Row className={styles.billing_row}>
                <Col form="full">
                  <Row>
                    <Col form="full">
                      <p>Billing Address</p>
                    </Col>
                  </Row>
                  <Row>
                    <Col form="full">
                      <TextInput
                        type="text"
                        id="card-billing-address-line-1"
                        name="card-billing-address-line-1"
                        placeholder="Address line 1"
                        autoComplete="address-line1"
                        onChange={(e) => handleBillingAddressChange('addressLine1', e.target.value)}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col form="full">
                      <TextInput
                        type="text"
                        id="card-billing-address-line-2"
                        name="card-billing-address-line-2"
                        placeholder="Address line 2"
                        autoComplete="address-line2"
                        onChange={(e) => handleBillingAddressChange('addressLine2', e.target.value)}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col form="full">
                      <TextInput
                        type="text"
                        id="card-billing-address-admin-area-line-1"
                        name="card-billing-address-admin-area-line-1"
                        placeholder="City"
                        autoComplete="address-level2"
                        onChange={(e) => handleBillingAddressChange('adminArea1', e.target.value)}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col form="full">
                      <TextInput
                        type="text"
                        id="card-billing-address-admin-area-line-2"
                        name="card-billing-address-admin-area-line-2"
                        placeholder="State"
                        autoComplete="address-level1"
                        onChange={(e) => handleBillingAddressChange('adminArea2', e.target.value)}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col form="full">
                      <TextInput
                        type="text"
                        id="card-billing-address-country-code"
                        name="card-billing-address-country-code"
                        autoComplete="country"
                        placeholder="Country code (eg. US, GB)"
                        onChange={(e) => handleBillingAddressChange('countryCode', e.target.value)}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col form="full">
                      <TextInput
                        type="text"
                        id="card-billing-address-postal-code"
                        name="card-billing-address-postal-code"
                        placeholder="Postal/zip code"
                        autoComplete="postal-code"
                        onChange={(e) => handleBillingAddressChange('postalCode', e.target.value)}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            )}
            <Row>
              <Col form="full">
                <SubmitPayment
                  isPaying={isPaying}
                  setIsPaying={setIsPaying}
                  {...(Object.values(billingAddress).filter((value) => value).length > 0 && {
                    billingAddress
                  })}
                />
              </Col>
            </Row>
          </Container>
        </PayPalCardFieldsProvider>
      )}
    </>
  );
};

CustomizedCardFieldsAndBillingAddress.propTypes = {
  handleCreateOrder: PropTypes.func.isRequired,
  handleOrderIntent: PropTypes.func.isRequired
};

export default CustomizedCardFieldsAndBillingAddress;
